<template>
  <section class="duallo-admin-table">
    <header class="duallo-table-header table-header">
      <div>
        <div class="duallo-eyebrow table-eyebrow">Tenant</div>
        <h3 class="duallo-table-title table-title">Review Requests</h3>
      </div>
      <div class="filter-chips">
        <button v-for="option in filterOptions" :key="option.value" type="button" class="filter-chip"
          :class="{ 'is-selected': statusFilter === option.value }" @click="setFilter(option.value)">
          {{ option.label }}
        </button>
      </div>
    </header>

    <div class="duallo-table-card table-card">
      <div v-if="isLoading" class="duallo-state-block state-block">
        <div v-for="n in 3" :key="n" class="duallo-row-skeleton row-skeleton" />
      </div>

      <div v-else-if="requests.length === 0" class="duallo-state-block state-block empty-state">
        <v-icon size="40" color="#4a5a60">mdi-clipboard-check-outline</v-icon>
        <p class="empty-title">No review requests</p>
        <p class="empty-subtitle">
          {{ statusFilter === 'Pending'
            ? 'Nothing is waiting for review — all caught up.'
            : 'No requests match this filter.' }}
        </p>
      </div>

      <div v-else-if="!isCompact" class="duallo-table-wrap">
        <table class="duallo-data-table">
          <thead>
            <tr>
              <th>Scan</th>
              <th>Asset</th>
              <th>Status</th>
              <th>Requested</th>
              <th>Reviewed</th>
              <th>Reason</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="request in requests" :key="request.scanId">
              <td>
                <div class="cell-name-main">{{ request.scanName || 'Unnamed scan' }}</div>
                <div class="cell-name-sub mono">{{ request.scanId }}</div>
              </td>
              <td>
                <div class="cell-name-main">{{ request.assetName || '—' }}</div>
                <div class="cell-name-sub">{{ request.organizationName || '' }}</div>
              </td>
              <td>
                <span class="review-badge" :class="badgeClass(request.status)">{{ request.status }}</span>
              </td>
              <td><span class="muted">{{ formatDate(request.requestedAt) }}</span></td>
              <td>
                <div v-if="request.reviewedAt">
                  <div>{{ request.reviewedByName || '—' }}</div>
                  <div class="cell-name-sub">{{ formatDate(request.reviewedAt) }}</div>
                </div>
                <span v-else class="muted">—</span>
              </td>
              <td>
                <span class="muted reason-cell">{{ request.reason || '—' }}</span>
              </td>
              <td class="cell-actions">
                <button type="button" class="duallo-btn duallo-btn--ghost" @click="openReview(request)">
                  <v-icon size="16">mdi-magnify-scan</v-icon><span>Review</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="duallo-card-list">
        <article v-for="request in requests" :key="request.scanId" class="review-card">
          <div class="review-card-head">
            <div class="review-card-title">
              <div class="cell-name-main">{{ request.scanName || 'Unnamed scan' }}</div>
              <div class="cell-name-sub mono">{{ request.scanId }}</div>
            </div>
            <span class="review-badge" :class="badgeClass(request.status)">{{ request.status }}</span>
          </div>

          <dl class="review-card-fields">
            <div class="review-field">
              <dt>Asset</dt>
              <dd>
                <div class="cell-name-main">{{ request.assetName || '—' }}</div>
                <div v-if="request.organizationName" class="cell-name-sub">{{ request.organizationName }}</div>
              </dd>
            </div>
            <div class="review-field">
              <dt>Requested</dt>
              <dd><span class="muted">{{ formatDate(request.requestedAt) }}</span></dd>
            </div>
            <div class="review-field">
              <dt>Reviewed</dt>
              <dd>
                <template v-if="request.reviewedAt">
                  <div>{{ request.reviewedByName || '—' }}</div>
                  <div class="cell-name-sub">{{ formatDate(request.reviewedAt) }}</div>
                </template>
                <span v-else class="muted">—</span>
              </dd>
            </div>
            <div class="review-field">
              <dt>Reason</dt>
              <dd><span class="muted reason-card">{{ request.reason || '—' }}</span></dd>
            </div>
          </dl>

          <div class="review-card-actions">
            <button type="button" class="duallo-btn duallo-btn--ghost" @click="openReview(request)">
              <v-icon size="16">mdi-magnify-scan</v-icon><span>Review</span>
            </button>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup>
import { defineOptions, ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import ScanReviewRequestService from '@/services/ScanReviewRequestService';

defineOptions({ name: 'ScanReviewRequestsTable' });

const router = useRouter();

const { smAndDown } = useDisplay();
const isCompact = computed(() => smAndDown.value);

const filterOptions = [
  { value: 'Pending', label: 'Pending' },
  { value: 'Accepted', label: 'Accepted' },
  { value: 'Rejected', label: 'Rejected' },
  { value: null, label: 'All' },
];

const statusFilter = ref('Pending');
const requests = ref([]);
const isLoading = ref(false);

const load = async () => {
  isLoading.value = true;
  try {
    requests.value = await ScanReviewRequestService.getReviewRequests(statusFilter.value);
  } catch (error) {
    console.error('Failed to load scan review requests:', error);
    requests.value = [];
  } finally {
    isLoading.value = false;
  }
};

const setFilter = async (value) => {
  statusFilter.value = value;
  await load();
};

const openReview = (request) => {
  router.push({ name: 'ProvenanceReview', params: { scanId: request.scanId } });
};

const badgeClass = (status) => ({
  'is-pending': status === 'Pending',
  'is-accepted': status === 'Accepted',
  'is-rejected': status === 'Rejected',
});

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

onMounted(load);
</script>

<style scoped>
/* ----- Overrides where this table differs from the shared defaults ----- */
.table-header { align-items: flex-end; }
.table-eyebrow { font-size: 11px; letter-spacing: 0.14em; color: #7e8f95; font-weight: 400; }
.table-title { font-size: 20px; }
.table-card { background: rgba(13, 27, 30, 0.85); border: 1px solid rgba(96, 125, 139, 0.18); padding: 6px; }

.duallo-data-table { font-size: 13.5px; }
.duallo-data-table thead th {
  padding: 10px 12px;
  letter-spacing: 0.08em;
  color: #7e8f95;
  font-weight: 700;
  background: transparent;
  border-bottom-color: rgba(96, 125, 139, 0.2);
}
.duallo-data-table tbody tr { border-bottom: none; }
.duallo-data-table tbody tr:hover { background: rgba(179, 157, 219, 0.05); }
.duallo-data-table td {
  padding: 10px 12px;
  vertical-align: top;
  font-size: 13.5px;
  border-bottom: 1px solid rgba(96, 125, 139, 0.1);
}

/* Purple-themed ghost button */
.duallo-btn--ghost {
  background: transparent;
  border-color: rgba(179, 157, 219, 0.25);
  color: #b0bec5;
  font-size: 12.5px;
  font-weight: 400;
}
.duallo-btn--ghost:hover { background: rgba(179, 157, 219, 0.1); color: #ffffff; }

/* Compact / centered state block */
.state-block { padding: 28px 16px; align-items: center; gap: 8px; }
.row-skeleton {
  width: 100%;
  height: 40px;
  background: rgba(96, 125, 139, 0.12);
  animation: pulse 1.4s ease-in-out infinite;
}
@keyframes pulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
.empty-title { margin: 6px 0 0; color: #eceff1; font-size: 15px; }
.empty-subtitle { margin: 0; color: #7e8f95; font-size: 13px; }

/* ----- Filter chips ----- */
.filter-chips { display: flex; gap: 6px; }
.filter-chip {
  padding: 6px 14px; border-radius: 999px; border: 1px solid rgba(179, 157, 219, 0.25);
  background: transparent; color: #b0bec5; font-size: 12.5px; cursor: pointer;
}
.filter-chip:hover { background: rgba(179, 157, 219, 0.08); color: #ffffff; }
.filter-chip.is-selected { background: rgba(171, 71, 188, 0.22); border-color: #ab47bc; color: #ffffff; }

/* ----- Cells ----- */
.cell-name-main { color: #ffffff; }
.cell-name-sub { font-size: 11.5px; color: #7e8f95; }
.mono { font-family: ui-monospace, monospace; font-size: 12px; }
.muted { color: #90a4ae; }
.reason-cell { display: inline-block; max-width: 260px; white-space: pre-wrap; }

/* ----- Status badges ----- */
.review-badge { padding: 3px 10px; border-radius: 999px; font-size: 12px; }
.review-badge.is-pending { background: rgba(171, 71, 188, 0.2); color: #ce93d8; }
.review-badge.is-accepted { background: rgba(76, 175, 80, 0.18); color: #a5d6a7; }
.review-badge.is-rejected { background: rgba(244, 67, 54, 0.16); color: #ef9a9a; }

.cell-actions { text-align: right; white-space: nowrap; }

/* ----- Mobile card list (smAndDown) ----- */
.review-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(96, 125, 139, 0.18);
  border-radius: 12px;
}

.review-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.review-card-title { min-width: 0; }

.review-card-fields {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
}

.review-field {
  display: grid;
  grid-template-columns: 84px 1fr;
  gap: 10px;
  align-items: baseline;
}

.review-field dt {
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7e8f95;
  font-weight: 700;
}

.review-field dd {
  margin: 0;
  min-width: 0;
  font-size: 13px;
  color: #cfd8dc;
}

.review-card-title .cell-name-sub,
.review-field dd .cell-name-sub { word-break: break-all; }

.reason-card {
  display: block;
  white-space: pre-wrap;
  word-break: break-word;
  min-width: 0;
}

.review-card-actions { display: flex; justify-content: flex-end; }
</style>
